import * as T from 'three';
// A swarm of little bats for the crackle bat. Each is a small copy of the
// toy: round body, pointed ears, glossy eyes and scalloped wings. The wings
// flap and fold in the vertex shader with one phase per bat, so one draw
// call draws them all.
// Flying bats circle a column above the toy, each at its own radius, height
// and speed with a little flutter, and keep apart with the separation rule of
// Reynolds' boids (SIGGRAPH 1987). They leave through a hole one after
// another, come back through it, and sit in a heap in the shell's bowl, or on
// the floor once the bowl has broken.
const clamp=(v,a,b)=>Math.max(a,Math.min(b,v));
const dist=(a,b)=>Math.hypot(a[0]-b[0],a[1]-b[1],a[2]-b[2]);
// Wing hinge (body units): wing vertices turn about the line x=±HINGE.
const HINGE=.78,TIP=3.45;
// One bat with a body radius of 1, facing +z, up +y, wings along x.
// flap: signed span from the hinge (0 off the wing); glow: glints.
// body: linear RGB of the fur, so a rare toy's bats match its colour.
export function cuteBatGeometry({body=[.20,.085,.42]}={}){
  const pos=[],col=[],flap=[],glow=[],idx=[];
  const membrane=[.05,.02,.12],bone=[.15,.075,.29],ink=[.012,.006,.016],white=[1,1,1],pink=[1,.42,.6];
  const add=(p,c,f=0,g=0)=>{pos.push(...p);col.push(...c);flap.push(f);glow.push(g);return pos.length/3-1;};
  const blob=(center,radii,c,g=0,seg=12,rings=8)=>{
    const start=pos.length/3;
    for(let j=0;j<=rings;j++)for(let i=0;i<=seg;i++){const v=j/rings*Math.PI,u=i/seg*Math.PI*2;add([center[0]+radii[0]*Math.sin(v)*Math.cos(u),center[1]+radii[1]*Math.cos(v),center[2]+radii[2]*Math.sin(v)*Math.sin(u)],c,0,g);}
    for(let j=0;j<rings;j++)for(let i=0;i<seg;i++){const a=start+j*(seg+1)+i,b=a+seg+1;idx.push(a,a+1,b,a+1,b+1,b);}
  };
  // A cone from a base ellipse (radii along side and front) to an apex.
  const cone=(base,axis,height,side,front,c,seg=10)=>{
    const a=new T.Vector3(...axis).normalize(),s=new T.Vector3().crossVectors(a,new T.Vector3(0,0,1)).normalize(),w=new T.Vector3().crossVectors(s,a);
    const apex=add(new T.Vector3(...base).addScaledVector(a,height).toArray(),c),ring=[];
    for(let i=0;i<seg;i++){const t=i/seg*Math.PI*2;ring.push(add(new T.Vector3(...base).addScaledVector(s,Math.cos(t)*side).addScaledVector(w,Math.sin(t)*front).toArray(),c));}
    for(let i=0;i<seg;i++)idx.push(apex,ring[(i+1)%seg],ring[i]);
  };
  blob([0,0,0],[1,1.02,.96],body,0,18,12);
  for(const s of [-1,1]){
    cone([s*.42,.66,.02],[s*.42,1,-.04],1.05,.34,.2,body);
    blob([s*.36,.18,.84],[.25,.3,.16],ink,0,12,8);
    blob([s*.29,.31,.98],[.085,.085,.05],white,1,8,5);
    blob([s*.62,-.12,.74],[.17,.09,.06],pink,0,10,6);
    cone([s*.13,-.36,.9],[0,-1,.25],.17,.055,.04,white,6);
    // Wing membrane, fanned from the wrist: the arm along the leading edge,
    // three scallops on the trailing edge. Cusps and the arm are lighter, so
    // the fan edges read as finger bones.
    const S=[.78,.28,.15],E=[1.75,.55,.35],W=[2.55,.6,.05],T1=[TIP,.35,-.5],T2=[2.95,.25,-1.15],T3=[2,.2,-1.3],B=[.85,.15,-.55];
    const edge=[[T1,bone]];
    for(const [a,b,end] of [[T1,T2,bone],[T2,T3,bone],[T3,B,membrane]]){
      const mid=a.map((n,k)=>(n+b[k])/2),control=mid.map((n,k)=>n+(W[k]-n)*.32);
      for(let k=1;k<=4;k++){const t=k/5;edge.push([a.map((n,j)=>(1-t)*(1-t)*n+2*t*(1-t)*control[j]+t*t*b[j]),membrane]);}
      edge.push([b,end]);
    }
    edge.push([S,bone],[E,bone]);
    const span=p=>s*Math.max(.001,clamp((p[0]-HINGE)/(TIP-HINGE),0,1));
    const wrist=add([s*W[0],W[1],W[2]],bone,span(W)),ring=edge.map(([p,c])=>add([s*p[0],p[1],p[2]],c,span(p)));
    for(let k=0;k<ring.length-1;k++)s>0?idx.push(wrist,ring[k],ring[k+1]):idx.push(wrist,ring[k+1],ring[k]);
  }
  const g=new T.BufferGeometry();
  g.setAttribute('position',new T.Float32BufferAttribute(pos,3));g.setAttribute('color',new T.Float32BufferAttribute(col,3));
  g.setAttribute('flap',new T.Float32BufferAttribute(flap,1));g.setAttribute('glow',new T.Float32BufferAttribute(glow,1));
  g.setIndex(idx);g.computeVertexNormals();return g;
}
// Wing pose shared by positions and normals: beat around a small dihedral,
// bending more toward the tip; folded wings hang down, close to the body.
const WING=`
  float wingSide=sign(flap),wingSpan=abs(flap);
  float wingAngle=mix(.15+aAmp*sin(batTime*30.+aPhase)*(.35+.65*wingSpan),-1.35,aFold);
  float wingCos=cos(wingAngle),wingSin=sin(wingAngle)*wingSide;`;
export class BatSwarm{
  // home: shell centre; size: shell radius; seat(i): where bat i sits now;
  // contain(p,acc): optional steering that keeps a flying bat in view;
  // obstacles(): spheres {c, r} to fly around (by default the shell).
  constructor(parent,{count=32,home,size,seat,contain,obstacles,body,random=Math.random}){
    this.random=random;this.home=[...home];this.size=size;this.count=count;this.seat=seat;this.contain=contain;
    const shell=[{c:[...home],r:size*1.05}];this.obstacles=obstacles??(()=>shell);
    this.clock=0;this.flying=0;this.door=null;this.unit=size*.07;
    const geometry=cuteBatGeometry({body});
    this.phase=new Float32Array(count).map(()=>random()*Math.PI*2);this.amp=new Float32Array(count);this.fold=new Float32Array(count).fill(1);
    geometry.setAttribute('aPhase',new T.InstancedBufferAttribute(this.phase,1));
    geometry.setAttribute('aAmp',new T.InstancedBufferAttribute(this.amp,1).setUsage(T.DynamicDrawUsage));
    geometry.setAttribute('aFold',new T.InstancedBufferAttribute(this.fold,1).setUsage(T.DynamicDrawUsage));
    const material=new T.MeshStandardMaterial({vertexColors:true,roughness:.42,side:T.DoubleSide});
    this.time={value:0};
    material.onBeforeCompile=shader=>{
      shader.uniforms.batTime=this.time;
      shader.vertexShader='attribute float flap;attribute float glow;attribute float aPhase;attribute float aAmp;attribute float aFold;uniform float batTime;varying float vGlow;\n'+shader.vertexShader
        .replace('#include <beginnormal_vertex>',`#include <beginnormal_vertex>
          if(flap!=0.){${WING}objectNormal.xy=vec2(wingCos*objectNormal.x-wingSin*objectNormal.y,wingSin*objectNormal.x+wingCos*objectNormal.y);}`)
        .replace('#include <begin_vertex>',`#include <begin_vertex>
          vGlow=glow;
          if(flap!=0.){${WING}
            float r=(abs(transformed.x)-${HINGE})*mix(1.,.3,aFold);
            transformed.x=wingSide*(${HINGE}+r*wingCos);transformed.y+=r*sin(wingAngle);transformed.z=mix(transformed.z,transformed.z*.45-.1,aFold);}`);
      shader.fragmentShader='varying float vGlow;\n'+shader.fragmentShader.replace('#include <emissivemap_fragment>','#include <emissivemap_fragment>\n  totalEmissiveRadiance+=vGlow*vec3(.9);');
    };
    material.customProgramCacheKey=()=>'cute-bat-v2';
    this.mesh=new T.InstancedMesh(geometry,material,count);this.mesh.name='little_bats';this.mesh.frustumCulled=false;this.mesh.castShadow=true;this.mesh.unpickable=true;
    for(let i=0;i<count;i++){const k=.86+.24*random();this.mesh.setColorAt(i,new T.Color(k,k*(.94+.12*random()),k));}
    parent.add(this.mesh);
    // Each bat flies its own circle: radius and height (in shell radii), speed.
    this.bats=Array.from({length:count},(_,i)=>({
      i,p:[...seat(i)],v:[0,0,0],mode:'sit',stage:0,delay:0,fold:1,
      radius:.9+.45*random(),lift:.35+1.35*random(),speed:1.25+.85*random(),bob:random()*Math.PI*2,fan:[0,0,0],place:random()*Math.PI*2,
      jitter:[0,0,0],aim:[0,0,0],next:0,scale:.85+.3*random(),q:new T.Quaternion(),scared:0,
    }));
    this.matrix=new T.Matrix4();this.look=new T.Matrix4();this.target=new T.Quaternion();this.forward=new T.Vector3();this.upward=new T.Vector3();this.origin=new T.Vector3();this.position=new T.Vector3();this.scale=new T.Vector3();
    for(const b of this.bats)this.orient(b,1);
    this.write();
  }
  get airborne(){return this.bats.some(b=>b.mode!=='sit');}
  get settling(){return this.bats.some(b=>Math.abs(b.fold-(b.mode==='sit'?1:0))>.01);}
  // Out through a hole one after another; bats already out scatter again.
  // door: {point, normal} on the shell.
  release(door){
    this.door=door;this.flying=0;
    const waiting=this.bats.filter(b=>b.mode==='sit').sort(()=>this.random()-.5),n=door.normal;
    // Each bat aims a little to one side of the hole's axis, so the stream fans out.
    const side=new T.Vector3(...n).cross(Math.abs(n[1])<.9?new T.Vector3(0,1,0):new T.Vector3(1,0,0)).normalize(),other=new T.Vector3(...n).cross(side);
    waiting.forEach((b,k)=>{
      b.mode='exit';b.stage=0;b.delay=k*.045+this.random()*.03;
      const a=this.random()*Math.PI*2,r=this.size*(.2+.45*this.random());b.fan=side.clone().multiplyScalar(Math.cos(a)*r).addScaledVector(other,Math.sin(a)*r).toArray();
    });
    for(const b of this.bats)if(b.mode==='return'){b.mode='fly';b.stage=0;}
  }
  recall(door){
    this.door=door;
    this.bats.filter(b=>b.mode==='fly').sort(()=>this.random()-.5).forEach((b,k)=>{b.mode='return';b.stage=0;b.delay=k*.055;});
  }
  // A finger along a ray (toy space, dir unit length) startles the flyers
  // near it: each darts away from the line and flaps hard for a moment.
  // Returns how many were startled.
  startle(origin,dir){
    const s=this.size,reach=.6*s;let count=0;
    for(const b of this.bats){
      if(!(b.mode==='fly'||b.mode==='return'&&b.stage===0))continue;
      const t=(b.p[0]-origin[0])*dir[0]+(b.p[1]-origin[1])*dir[1]+(b.p[2]-origin[2])*dir[2];
      const off=[0,1,2].map(a=>b.p[a]-origin[a]-dir[a]*t),d=Math.hypot(...off);
      if(d>=reach)continue;
      // Right on the line: dodge sideways, across the ray.
      const side=d>1e-6?null:new T.Vector3(...dir).cross(new T.Vector3(Math.abs(dir[1])<.9?0:1,Math.abs(dir[1])<.9?1:0,0)).normalize().toArray();
      const away=side??off.map(v=>v/d),push=(.55+.45*(1-d/reach))*2.6*s;
      for(let a=0;a<3;a++)b.v[a]=b.v[a]*.3+away[a]*push;
      b.v[1]+=.35*s;b.scared=.6;b.next=this.clock+.35;b.aim=away.map(v=>v*1.2*s);count++;
    }
    return count;
  }
  // Seek with arrival (Reynolds, GDC 1999): full speed far off, slowing near the target.
  steer(b,target,speed,acc,gain=6,around=false){
    const d=[target[0]-b.p[0],target[1]-b.p[1],target[2]-b.p[2]],l=Math.hypot(...d)||1e-6,k=Math.min(speed,l*3)/l,want=d.map(v=>v*k);
    if(around)this.deflect(b,want);
    for(let a=0;a<3;a++)acc[a]+=(want[a]-b.v[a])*gain;
  }
  // Circle a column above the toy, wider than the shell at each height. Each
  // bat has its own place on a slowly turning ring, so the flock spreads all
  // the way round instead of travelling as one clump. The ring turns
  // counter-clockwise seen from above, as free-tailed bats spiral out of
  // Carlsbad Caverns (NPS).
  cruise(b,acc,h){
    const s=this.size,home=this.home,turn=-.9;
    const y=home[1]+s*(b.lift+.22*Math.sin(this.clock*.7+b.bob)),dy=(y-home[1])/(s*1.05),shell=Math.abs(dy)<1?Math.sqrt(1-dy*dy):0;
    const r=s*Math.max(b.radius,shell+.4),a=b.place+this.clock*turn,c=Math.cos(a),n=Math.sin(a);
    const want=[-n*turn*r+(home[0]+c*r-b.p[0])*2,(y-b.p[1])*2,c*turn*r+(home[2]+n*r-b.p[2])*2],l=Math.hypot(...want),most=b.speed*s;
    for(let k=0;k<3;k++)want[k]*=Math.min(1,most/(l||1));
    this.deflect(b,want);
    for(let k=0;k<3;k++)acc[k]+=(want[k]-b.v[k])*2.5;
    // Flutter: a fresh small push every quarter second or so, eased in.
    if(this.clock>=b.next){b.next=this.clock+.18+.2*this.random();const u=this.random()*2-1,t=this.random()*Math.PI*2,q=Math.sqrt(1-u*u);b.aim=[q*Math.cos(t)*1.8*s,u*1.2*s,q*Math.sin(t)*1.8*s];}
    for(let a=0;a<3;a++){b.jitter[a]+=(b.aim[a]-b.jitter[a])*Math.min(1,h*8);acc[a]+=b.jitter[a];}
    this.separate(b,acc);
  }
  separate(b,acc){
    const s=this.size,reach=.3*s;
    for(const o of this.bats){
      if(o===b||o.mode==='sit')continue;const d=dist(b.p,o.p);
      if(d<reach&&d>1e-6)for(let a=0;a<3;a++)acc[a]+=(b.p[a]-o.p[a])/d*(1-d/reach)*5*s;
    }
  }
  // Near an obstacle, drop the part of the wanted velocity that heads into
  // it, so the bat slides round it instead of flying through.
  deflect(b,want){
    const margin=.4*this.size;
    for(const o of this.obstacles()){
      const d=dist(b.p,o.c),reach=o.r+margin;if(d>=reach||d<1e-6)continue;
      const n=[(b.p[0]-o.c[0])/d,(b.p[1]-o.c[1])/d,(b.p[2]-o.c[2])/d],inward=want[0]*n[0]+want[1]*n[1]+want[2]*n[2];
      if(inward<0){const k=Math.min(1,(reach-d)/margin);for(let a=0;a<3;a++)want[a]-=n[a]*inward*k;}
    }
  }
  // Push out of any obstacle; a bat heading for the hole may come close
  // inside the cone around it.
  clear(b,acc,door=false){
    const home=this.home,dh=dist(b.p,home);
    if(door&&this.door&&dh>1e-6&&((b.p[0]-home[0])*this.door.normal[0]+(b.p[1]-home[1])*this.door.normal[1]+(b.p[2]-home[2])*this.door.normal[2])/dh>.8)return;
    for(const o of this.obstacles()){
      const d=dist(b.p,o.c),reach=o.r+.08*this.size;if(d>=reach||d<1e-6)continue;
      for(let a=0;a<3;a++)acc[a]+=(b.p[a]-o.c[a])/d*(reach-d)*40;
    }
  }
  step(dt){
    if(!(dt>0))return;const h=Math.min(dt,1/30),s=this.size;this.clock+=h;this.time.value=this.clock;
    if(this.bats.some(b=>b.mode==='fly'))this.flying+=h;
    for(const b of this.bats){
      const acc=[0,0,0];let foldTo=0;b.scared=Math.max(0,b.scared-h);
      if(b.mode==='sit'){
        const seat=this.seat(b.i);for(let a=0;a<3;a++)b.p[a]+=(seat[a]-b.p[a])*Math.min(1,h*10);
        b.v=[0,0,0];b.fold+=(1-b.fold)*Math.min(1,h*8);this.orient(b,h);continue;
      }
      if(b.mode==='exit'){
        if((b.delay-=h)>0){b.fold+=(1-b.fold)*Math.min(1,h*8);this.orient(b,h);continue;}
        const door=this.door;
        if(b.stage===0){this.steer(b,door.point,2.3*s,acc,9);if(dist(b.p,door.point)<.2*s)b.stage=1;}
        else{const out=door.point.map((n,a)=>n+door.normal[a]*.9*s+b.fan[a]);this.steer(b,out,2.4*s,acc,7);if(dist(b.p,out)<.25*s||dist(b.p,this.home)>1.3*s)b.mode='fly';}
      }else if(b.mode==='fly'){
        this.cruise(b,acc,h);this.clear(b,acc);
      }else if(b.mode==='return'){
        const door=this.door;
        if(b.delay>0){b.delay-=h;this.cruise(b,acc,h);this.clear(b,acc);}
        else if(b.stage===0){
          const near=door.point.map((n,a)=>n+door.normal[a]*.7*s);this.steer(b,near,1.7*s,acc,5,true);this.separate(b,acc);this.clear(b,acc,true);
          if(dist(b.p,near)<.22*s)b.stage=1;
        }else if(b.stage===1){this.steer(b,door.point,1.2*s,acc,7);if(dist(b.p,door.point)<.16*s)b.stage=2;}
        else{
          const seat=this.seat(b.i),d=dist(b.p,seat);this.steer(b,seat,Math.min(1.1*s,d*3.5),acc,8);
          foldTo=clamp(1-d/(.5*s),0,1);
          if(d<.03*s){b.mode='sit';b.p=[...seat];b.v=[0,0,0];this.orient(b,h);continue;}
        }
      }
      if(b.mode==='fly'||b.mode==='return'&&b.stage===0){
        if(b.p[1]<.3*s)acc[1]+=(.3*s-b.p[1])*30;
        this.contain?.(b.p,acc);
      }
      for(let a=0;a<3;a++)b.v[a]+=acc[a]*h;
      const speed=Math.hypot(...b.v),most=2.6*s,least=b.mode==='fly'?.6*s:0;
      if(speed>most)b.v=b.v.map(v=>v*most/speed);else if(speed<least&&speed>1e-6)b.v=b.v.map(v=>v*least/speed);
      for(let a=0;a<3;a++)b.p[a]+=b.v[a]*h;
      b.fold+=(foldTo-b.fold)*Math.min(1,h*10);b.bank=acc;this.orient(b,h);
    }
    this.write();
  }
  // Flyers face where they go and bank into turns; sitting bats face front,
  // turned a little away from the middle of the heap.
  orient(b,h){
    const s=this.size;
    if(b.mode==='sit'||b.mode==='exit'&&b.delay>0||b.fold>.6){
      const seat=this.seat(b.i),out=[seat[0]-this.home[0],0,seat[2]-this.home[2]];
      this.forward.set(out[0]*.6/s,0,1+out[2]*.6/s).normalize();this.upward.set(0,1,0);
    }else{
      const speed=Math.hypot(...b.v);this.forward.set(...(speed>1e-4?b.v:[0,0,1])).normalize();
      const bank=b.bank??[0,0,0],side=new T.Vector3(...bank);side.addScaledVector(this.forward,-side.dot(this.forward));
      this.upward.set(0,1,0).addScaledVector(side,.12/s).normalize();
    }
    // Matrix4.lookAt points +z from the target to the eye, so look from the heading back to the origin.
    this.look.lookAt(this.forward,this.origin,this.upward);this.target.setFromRotationMatrix(this.look);
    b.q.slerp(this.target,Math.min(1,h*12));
  }
  write(){
    for(let i=0;i<this.count;i++){
      const b=this.bats[i];
      this.mesh.setMatrixAt(i,this.matrix.compose(this.position.fromArray(b.p),b.q,this.scale.setScalar(this.unit*b.scale)));
      this.amp[i]=(1-b.fold)*(b.scared>0?1.3:b.mode==='exit'?1:.85);this.fold[i]=b.fold;
    }
    this.mesh.instanceMatrix.needsUpdate=true;this.mesh.geometry.attributes.aAmp.needsUpdate=true;this.mesh.geometry.attributes.aFold.needsUpdate=true;
  }
  reset(){for(const b of this.bats){b.scared=0;b.mode='sit';b.stage=0;b.p=[...this.seat(b.i)];b.v=[0,0,0];b.fold=1;this.orient(b,1);}this.door=null;this.flying=0;this.write();}
  dispose(){this.mesh.parent?.remove(this.mesh);this.mesh.geometry.dispose();this.mesh.material.dispose();}
}
